import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useNotifications } from '@jira/polaris__lib-notifications/src/useNotifications.tsx';
import { JiraProductDiscoveryViewAri } from '@atlassian/ari/jira-product-discovery/view';
import { JiraProductDiscoveryGlobalViewAri } from '@atlassian/ari/jira-product-discovery/global-view';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import type { useCloneViewIntoGlobalMutation } from './__generated__/useCloneViewIntoGlobalMutation.graphql.ts';

const failureTitleMessage = defineMessage({
	id: 'polaris-ideas.view-header.clone-view-into-global.error-title',
	defaultMessage: "We couldn't save as global view",
	description: 'Title for the error notification after failing to save a view as a global view',
});
const savingTitleMessage = defineMessage({
	id: 'polaris-ideas.view-header.clone-view-into-global.saving-title',
	defaultMessage: 'Saving view as global...',
	description: 'Title for the loading notification shown while saving a view as a global view',
});
const successTitleMessage = defineMessage({
	id: 'polaris-ideas.view-header.clone-view-into-global.success-title',
	defaultMessage: 'Saved as a global view',
	description: 'Title for the success notification after saving a view as a global view',
});

export const useCloneViewIntoGlobal = (): ((viewUUID: string) => void) => {
	const { error: showError, customFlag, success: showSuccess } = useNotifications();
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const activationId = useActivationId();

	const [commitClone] = useMutation<useCloneViewIntoGlobalMutation>(graphql`
		mutation useCloneViewIntoGlobalMutation($input: JpdViewsServiceCloneViewIntoGlobalInput!) {
			jpdViewsService_cloneViewIntoGlobal(input: $input) {
				errors {
					message
				}
				success
				node {
					id
				}
			}
		}
	`);

	return useCallback(
		(viewUUID: string) => {
			const viewAri = JiraProductDiscoveryViewAri.create({
				siteId: cloudId,
				viewId: viewUUID,
				activationId,
			}).toString();
			const dismissSavingFlag = customFlag({ title: formatMessage(savingTitleMessage) });
			commitClone({
				// viewAri is constructed from tenant cloudId and view UUID, not from relay store
				// eslint-disable-next-line jira/relay/mutation-variables-from-relay-data
				variables: { input: { id: viewAri } },
				onCompleted: (data) => {
					const payload = data.jpdViewsService_cloneViewIntoGlobal;
					if (!payload?.success) {
						fireErrorAnalytics({
							error: new Error('cloneViewIntoGlobal mutation returned success=false'),
							meta: {
								id: 'cloneViewIntoGlobal',
								packageName: 'polaris-component-clone-view-into-global',
								teamName: 'jpd-aurora',
							},
						});
						showError({ title: formatMessage(failureTitleMessage) });
						return;
					}

					const globalViewAri = payload?.node?.id;
					if (globalViewAri) {
						const globalViewId =
							JiraProductDiscoveryGlobalViewAri.parse(globalViewAri).globalViewId;
						dismissSavingFlag();
						showSuccess({ title: formatMessage(successTitleMessage) });
						globalThis.window?.open(
							`/jira/settings/products/jira-product-discovery/global-views/${globalViewId}`,
							'_blank',
						);
					}
				},
				onError: (err) => {
					dismissSavingFlag();
					fireErrorAnalytics({
						error: err,
						meta: {
							id: 'cloneViewIntoGlobal',
							packageName: 'polaris-component-clone-view-into-global',
							teamName: 'jpd-aurora',
						},
					});
					showError({ title: formatMessage(failureTitleMessage) });
				},
			});
		},
		[activationId, cloudId, commitClone, customFlag, formatMessage, showError, showSuccess],
	);
};
